import {
  isEmpty,
  isNil,
  isPlainObject as lodashIsPlainObject,
  snakeCase,
} from 'lodash';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type ErrorsState = Record<string, unknown> | undefined;
export type FormulaErrors = Record<string, unknown> | undefined;

interface ColumbiaErrorsContextValue {
  errors: ErrorsState;
  setErrors: (errors: ErrorsState) => void;
  getErrors: (controllerName: string) => FormulaErrors;
  getFirstErrorMessage: (controllerPath: string) => string | undefined;
}

interface ColumbiaErrorsProviderProps {
  children: ReactNode;
}

interface ParsedPath {
  stepIndex?: number;
  componentIndex?: number;
  optionIndex?: number;
  subformComponentIndex?: number;
  subFormIndex?: number;
  subFormComponentIndex?: number;
}

const ColumbiaErrorsContext = createContext<ColumbiaErrorsContextValue | null>(
  null
);

const PATH_PATTERNS = {
  step: /steps[.[\]](\d+)/,
  component: /steps[.[\]]\d+[.\]]components[.[](\d+)/,
  option: /options[.[](\d+)/,
  subformComponent: /options[.[](\d+)[.\]]]?\.?components[.[](\d+)/,
  subForm: /subForms[.[](\d+)/,
  subFormComponent: /subForms[.[](\d+)[.\]]]?\.?components[.[](\d+)/,
} as const;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  lodashIsPlainObject(value);

const isEmptyObject = (value: unknown): boolean =>
  isPlainObject(value) && isEmpty(value);

const isValidErrorObject = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value) && !isEmpty(value);

const getArrayElement = <T,>(array: unknown, index: number): T | undefined => {
  if (!Array.isArray(array)) return undefined;
  const element = array[index] as unknown;
  return isValidErrorObject(element) ? (element as T) : undefined;
};

const parseMatchIndex = (
  match: RegExpExecArray | null,
  group = 1
): number | undefined =>
  match ? Number.parseInt(match[group], 10) : undefined;

const parsePath = (path: string): ParsedPath => ({
  stepIndex: parseMatchIndex(PATH_PATTERNS.step.exec(path)),
  componentIndex: parseMatchIndex(PATH_PATTERNS.component.exec(path)),
  optionIndex: parseMatchIndex(PATH_PATTERNS.option.exec(path)),
  subformComponentIndex: parseMatchIndex(
    PATH_PATTERNS.subformComponent.exec(path),
    2
  ),
  subFormIndex: parseMatchIndex(PATH_PATTERNS.subForm.exec(path)),
  subFormComponentIndex: parseMatchIndex(
    PATH_PATTERNS.subFormComponent.exec(path),
    2
  ),
});

const getErrorsForPath = (
  errors: ErrorsState,
  controllerName: string
): FormulaErrors => {
  if (!isPlainObject(errors)) return undefined;

  const additionalConfig = errors.additional_config;
  if (!isPlainObject(additionalConfig)) return undefined;

  const steps = additionalConfig.steps;
  if (!Array.isArray(steps)) return undefined;

  const parsed = parsePath(controllerName);

  if (isNil(parsed.stepIndex)) return undefined;

  const stepErrors = steps[parsed.stepIndex] as unknown;
  if (!stepErrors || isEmptyObject(stepErrors)) return undefined;

  if (isNil(parsed.componentIndex)) return stepErrors as FormulaErrors;

  const componentErrors = getArrayElement<Record<string, unknown>>(
    (stepErrors as Record<string, unknown>).components,
    parsed.componentIndex
  );
  if (!componentErrors) return undefined;

  // Handle subForms path (contacts/beneficiaries)
  if (!isNil(parsed.subFormIndex)) {
    const subFormErrors = getArrayElement<Record<string, unknown>>(
      componentErrors.sub_forms,
      parsed.subFormIndex
    );
    if (!subFormErrors) return undefined;
    if (isNil(parsed.subFormComponentIndex)) return subFormErrors;

    return getArrayElement<Record<string, unknown>>(
      subFormErrors.components,
      parsed.subFormComponentIndex
    );
  }

  // Handle options path (multi-select with subform)
  if (isNil(parsed.optionIndex)) return componentErrors;

  const optionErrors = getArrayElement<Record<string, unknown>>(
    componentErrors.options,
    parsed.optionIndex
  );
  if (!optionErrors) return undefined;
  if (isNil(parsed.subformComponentIndex)) return optionErrors;

  return getArrayElement<Record<string, unknown>>(
    optionErrors.components,
    parsed.subformComponentIndex
  );
};

const extractFieldAndComponentPath = (
  controllerPath: string
): { componentPath: string; fieldName: string } | undefined => {
  const lastDotIndex = controllerPath.lastIndexOf('.');
  if (lastDotIndex === -1) return undefined;

  return {
    componentPath: controllerPath.slice(0, lastDotIndex),
    fieldName: controllerPath.slice(lastDotIndex + 1),
  };
};

export const ColumbiaErrorsProvider = ({
  children,
}: ColumbiaErrorsProviderProps) => {
  const [errors, setErrors] = useState<ErrorsState>(undefined);

  const getErrors = useCallback(
    (controllerName: string): FormulaErrors =>
      getErrorsForPath(errors, controllerName),
    [errors]
  );

  const getFirstErrorMessage = useCallback(
    (controllerPath: string): string | undefined => {
      const parsed = extractFieldAndComponentPath(controllerPath);
      if (!parsed) return undefined;

      const componentErrors = getErrorsForPath(errors, parsed.componentPath);
      if (!componentErrors) return undefined;

      const errorArray = componentErrors[snakeCase(parsed.fieldName)];

      return Array.isArray(errorArray) && errorArray.length > 0
        ? (errorArray[0] as string | undefined)
        : undefined;
    },
    [errors]
  );

  const value = useMemo(
    () => ({ errors, setErrors, getErrors, getFirstErrorMessage }),
    [errors, getErrors, getFirstErrorMessage]
  );

  return (
    <ColumbiaErrorsContext.Provider value={value}>
      {children}
    </ColumbiaErrorsContext.Provider>
  );
};

export const useColumbiaErrors = (): ColumbiaErrorsContextValue => {
  const context = useContext(ColumbiaErrorsContext);
  if (!context) {
    throw new Error(
      'useColumbiaErrors must be used within a ColumbiaErrorsProvider'
    );
  }
  return context;
};
